import React from 'react';
import { BookOpen, Clock, Activity, Heart, TrendingUp, Info } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const WatchAndWaitPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Clock className="h-8 w-8 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-primary mb-1">Information & Education</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Understanding Watch and Wait
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="Understanding Watch and Wait"
            category="CLL"
            sourcePage="Understanding Watch and Wait"
            sourceUrl="/watch-and-wait"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Section 1: What does Watch and Wait mean */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            What does "Watch and Wait" mean?
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              "Watch and Wait" (sometimes called Active Surveillance) is a common approach used in CLL when the disease is stable and not causing problems.
            </p>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 my-4">
              <p className="font-medium text-blue-900 mb-2">It does not mean nothing is happening.</p>
              <p className="text-blue-800">It means your healthcare team is:</p>
              <ul className="mt-3 space-y-2 ml-1">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-blue-800">Monitoring your blood results</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-blue-800">Checking for symptoms</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-blue-800">Watching for meaningful changes over time</span>
                </li>
              </ul>
            </div>
            <p className="font-medium text-slate-800">
              Treatment begins only if and when it's needed.
            </p>
          </div>
        </div>

        {/* Section 2: Why treatment isn't started straight away */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0 mt-1">
              <BookOpen className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Why treatment isn't started straight away
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Research has shown that starting treatment early — before symptoms or progression — does not improve long-term outcomes for most people with CLL.
                </p>
                <p className="mb-3">Because treatments can have side effects, doctors aim to:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Preserve quality of life</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Avoid unnecessary treatment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Act only when benefits clearly outweigh risks</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: What is usually monitored */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0 mt-1">
              <TrendingUp className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                What is usually monitored?
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">During Watch and Wait, doctors often track:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>White blood cell trends</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Lymphocyte levels</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Platelets and haemoglobin</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Physical symptoms</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Lymph node changes</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  Patterns over time are far more important than single test results.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: The emotional side */}
        <div className="p-6 md:p-8 bg-amber-50/70">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0 mt-1">
              <Heart className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                The emotional side of Watch and Wait
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Many people find Watch and Wait emotionally challenging. It can feel unsettling to "do nothing" after a cancer diagnosis.
                </p>
                <p className="text-lg font-medium text-amber-800 my-4">
                  That feeling is valid.
                </p>
                <p className="mb-3">Some people cope by:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Learning more about their blood markers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Tracking results visually</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Keeping notes and questions for appointments</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Focusing on overall wellbeing</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  This platform is designed to support that process — quietly and privately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Note Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational note</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              Watch and Wait is a clinical decision guided by your medical team. This page provides context only.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="Understanding Watch and Wait"
          category="CLL"
          sourcePage="Understanding Watch and Wait"
          sourceUrl="/watch-and-wait"
        />
      </div>
    </div>
  );
};

export default WatchAndWaitPage;
